import React, { useState, useEffect, useRef } from 'react';
import { apiClient } from '../../shared/utils/api';
import { MetricCard } from '@bafkal/ui-shared/charts';
import { FormField, TextInput, Select } from '@bafkal/ui-shared/forms';
import { Card, Table, ErrorMessage, Button } from '@bafkal/ui-shared/components';
import { extractContractError } from '@bafkal/ui-shared/utils';
import { colors } from '@bafkal/ui-shared/tokens';

// Format USD currency amount
const formatUSD = (amount) =>
    `$${Number(amount).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// Transaction status badge using design token colours
const transactionStatusBadge = (status) => {
    const style = {
        completed: { bg: colors.success[50], color: colors.success[700] },
        pending:   { bg: colors.warning[50], color: colors.warning[700] },
    }[status] || { bg: colors.neutral[100], color: colors.neutral[700] };

    return (
        <span style={{
            display: 'inline-flex',
            alignItems: 'center',
            padding: '2px 8px',
            borderRadius: '9999px',
            fontSize: '0.75rem',
            fontWeight: 600,
            backgroundColor: style.bg,
            color: style.color,
            textTransform: 'capitalize',
        }}>
            {status}
        </span>
    );
};

const PaymentSettings = () => {
    const [loading, setLoading] = useState(false);
    const [saveLoading, setSaveLoading] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    const [serviceAvailable, setServiceAvailable] = useState(true);

    const [paymentInfo, setPaymentInfo] = useState({
        paymentMethod: '',
        accountName: '',
        accountNumber: '',
        bankName: '',
        routingNumber: '',
        paypalEmail: '',
        address: {
            street: '',
            city: '',
            state: '',
            postalCode: '',
            country: ''
        },
        taxId: ''
    });

    const [earnings, setEarnings] = useState({
        currentBalance: 0,
        pendingPayments: 0,
        nextPaymentDate: null,
        recentTransactions: []
    });

    const isMounted = useRef(true);

    useEffect(() => {
        const loadPaymentSettings = async () => {
            if (isMounted.current) setLoading(true);
            try {
                const [settingsResponse, earningsResponse] = await Promise.all([
                    apiClient.get('/payment/settings'),
                    apiClient.get('/payment/earnings')
                ]);

                if (isMounted.current) {
                    setPaymentInfo(settingsResponse.data);
                    setEarnings(earningsResponse.data);
                    setError(null);
                }
            } catch (err) {
                if (isMounted.current) {
                    if (err.response?.status === 404) {
                        // Publisher Payout service is not yet available — not an error.
                        setServiceAvailable(false);
                    } else {
                        const contractError = extractContractError(err, 'Failed to load payment settings');
                        setError(contractError.message);
                    }
                }
            } finally {
                if (isMounted.current) setLoading(false);
            }
        };

        loadPaymentSettings();

        return () => {
            isMounted.current = false;
        };
    }, []);

    const handleChange = (e) => {
        const { name, value } = e.target;
        if (name.includes('.')) {
            const [parent, child] = name.split('.');
            setPaymentInfo(prev => ({
                ...prev,
                [parent]: {
                    ...prev[parent],
                    [child]: value
                }
            }));
        } else {
            setPaymentInfo(prev => ({ ...prev, [name]: value }));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (isMounted.current) {
            setSaveLoading(true);
            setSuccess(null);
            setError(null);
        }

        try {
            await apiClient.put('/payment/settings', paymentInfo);
            if (isMounted.current) setSuccess('Payment settings updated successfully');
        } catch (err) {
            if (isMounted.current) {
                const contractError = extractContractError(err, 'Failed to update payment settings');
                setError(contractError.message);
            }
        } finally {
            if (isMounted.current) setSaveLoading(false);
        }
    };

    // Service unavailable state
    if (!serviceAvailable) {
        return (
            <div className="py-8">
                <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
                    <h1 className="text-2xl font-semibold text-gray-900 mb-4">Publisher Payout</h1>
                    <Card>
                        <div style={{ textAlign: 'center', padding: '32px 0' }}>
                            <svg style={{ margin: '0 auto 16px', width: '48px', height: '48px', color: colors.neutral[400] }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
                            </svg>
                            <h3 style={{ fontWeight: 500, marginBottom: '4px' }}>Payout settings not yet available</h3>
                            <p style={{ fontSize: '0.875rem', color: colors.neutral[500] }}>
                                Publisher payout configuration will be available once the service is launched.
                            </p>
                        </div>
                    </Card>
                </div>
            </div>
        );
    }

    // Loading state
    if (loading) {
        return (
            <div className="py-8">
                <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="animate-pulse space-y-4">
                        <div className="h-8 bg-gray-200 rounded w-1/4"></div>
                        <div className="space-y-3">
                            <div className="h-4 bg-gray-200 rounded"></div>
                            <div className="h-4 bg-gray-200 rounded"></div>
                            <div className="h-4 bg-gray-200 rounded"></div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    // Transactions table columns
    const transactionColumns = [
        {
            key: 'date',
            header: 'Date',
            render: (_, t) => new Date(t.date).toLocaleDateString(),
        },
        {
            key: 'type',
            header: 'Type',
            render: (_, t) => <span style={{ textTransform: 'capitalize' }}>{t.type}</span>,
        },
        {
            key: 'amount',
            header: 'Amount',
            render: (_, t) => formatUSD(t.amount),
        },
        {
            key: 'status',
            header: 'Status',
            render: (_, t) => transactionStatusBadge(t.status),
        },
    ];

    return (
        <div className="py-8">
            <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8" style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>

                {/* ── Earnings summary ── */}
                <Card title="Earnings Summary">
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '16px', marginBottom: earnings.recentTransactions.length > 0 ? '24px' : 0 }}>
                        <MetricCard
                            title="Current Balance"
                            value={formatUSD(earnings.currentBalance)}
                            iconBg={colors.success[50]}
                            icon={
                                <svg style={{ width: '20px', height: '20px', color: colors.success[500] }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                            }
                        />
                        <MetricCard
                            title="Pending Payments"
                            value={formatUSD(earnings.pendingPayments)}
                            iconBg={colors.primary[50]}
                            icon={
                                <svg style={{ width: '20px', height: '20px', color: colors.primary[600] }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                            }
                        />
                        <MetricCard
                            title="Next Payment"
                            value={earnings.nextPaymentDate
                                ? new Date(earnings.nextPaymentDate).toLocaleDateString()
                                : 'N/A'}
                            iconBg={colors.primary[100]}
                            icon={
                                <svg style={{ width: '20px', height: '20px', color: colors.primary[700] }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                </svg>
                            }
                        />
                    </div>

                    {/* Recent transactions */}
                    {earnings.recentTransactions.length > 0 && (
                        <>
                            <h4 style={{ fontSize: '0.875rem', fontWeight: 600, color: colors.neutral[900], marginBottom: '12px' }}>
                                Recent Transactions
                            </h4>

                            {/* Mobile card view */}
                            <div className="sm:hidden" style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginBottom: '8px' }}>
                                {earnings.recentTransactions.map((t) => (
                                    <div key={t.id} style={{ backgroundColor: colors.neutral[50], border: `1px solid ${colors.neutral[200]}`, borderRadius: '8px', padding: '12px' }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
                                            <div>
                                                <div style={{ fontSize: '0.875rem', fontWeight: 500 }}>{new Date(t.date).toLocaleDateString()}</div>
                                                <div style={{ fontSize: '0.75rem', color: colors.neutral[500], textTransform: 'capitalize' }}>{t.type}</div>
                                            </div>
                                            {transactionStatusBadge(t.status)}
                                        </div>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                                            <span style={{ fontSize: '0.75rem', color: colors.neutral[400], textTransform: 'uppercase', letterSpacing: '0.05em' }}>Amount</span>
                                            <span style={{ fontWeight: 500 }}>{formatUSD(t.amount)}</span>
                                        </div>
                                    </div>
                                ))}
                            </div>

                            {/* Desktop table */}
                            <div className="hidden sm:block">
                                <Table
                                    columns={transactionColumns}
                                    data={earnings.recentTransactions}
                                    keyField="id"
                                    emptyMessage="No recent transactions."
                                />
                            </div>
                        </>
                    )}
                </Card>

                {/* ── Payment settings form ── */}
                <Card title="Payment Settings">
                    <ErrorMessage message={error} />

                    {success && (
                        <div style={{ backgroundColor: colors.success[50], borderLeft: `4px solid ${colors.success[500]}`, padding: '12px 16px', borderRadius: '6px', marginBottom: '16px' }}>
                            <p style={{ margin: 0, color: colors.success[700], fontWeight: 500 }}>{success}</p>
                        </div>
                    )}

                    <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column' }}>
                        {/* Payment method */}
                        <FormField label="Payment Method">
                            <Select
                                id="paymentMethod"
                                name="paymentMethod"
                                value={paymentInfo.paymentMethod}
                                onChange={handleChange}
                            >
                                <option value="">Select a payment method</option>
                                <option value="bank">Bank Transfer</option>
                                <option value="paypal">PayPal</option>
                                <option value="wise">Wise (TransferWise)</option>
                            </Select>
                        </FormField>

                        {/* Bank fields */}
                        {paymentInfo.paymentMethod === 'bank' && (
                            <>
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 16px' }}>
                                    <FormField label="Account Holder Name">
                                        <TextInput name="accountName" id="accountName" value={paymentInfo.accountName} onChange={handleChange} />
                                    </FormField>
                                    <FormField label="Account Number">
                                        <TextInput name="accountNumber" id="accountNumber" value={paymentInfo.accountNumber} onChange={handleChange} />
                                    </FormField>
                                    <FormField label="Bank Name">
                                        <TextInput name="bankName" id="bankName" value={paymentInfo.bankName} onChange={handleChange} />
                                    </FormField>
                                    <FormField label="Routing Number">
                                        <TextInput name="routingNumber" id="routingNumber" value={paymentInfo.routingNumber} onChange={handleChange} />
                                    </FormField>
                                </div>
                            </>
                        )}

                        {/* PayPal fields */}
                        {paymentInfo.paymentMethod === 'paypal' && (
                            <FormField label="PayPal Email">
                                <TextInput type="email" name="paypalEmail" id="paypalEmail" value={paymentInfo.paypalEmail} onChange={handleChange} />
                            </FormField>
                        )}

                        {/* Billing address */}
                        <div style={{ borderTop: `1px solid ${colors.neutral[200]}`, paddingTop: '16px', marginTop: '8px', marginBottom: '8px' }}>
                            <h4 style={{ fontSize: '0.875rem', fontWeight: 600, color: colors.neutral[900], marginBottom: '12px', marginTop: 0 }}>
                                Billing Address
                            </h4>
                        </div>

                        <FormField label="Street Address">
                            <TextInput name="address.street" id="address.street" value={paymentInfo.address.street} onChange={handleChange} />
                        </FormField>

                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0 16px' }}>
                            <FormField label="City">
                                <TextInput name="address.city" id="address.city" value={paymentInfo.address.city} onChange={handleChange} />
                            </FormField>
                            <FormField label="State / Province">
                                <TextInput name="address.state" id="address.state" value={paymentInfo.address.state} onChange={handleChange} />
                            </FormField>
                            <FormField label="ZIP / Postal Code">
                                <TextInput name="address.postalCode" id="address.postalCode" value={paymentInfo.address.postalCode} onChange={handleChange} />
                            </FormField>
                        </div>

                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 16px' }}>
                            <FormField label="Country">
                                <Select name="address.country" id="address.country" value={paymentInfo.address.country} onChange={handleChange}>
                                    <option value="">Select a country</option>
                                    <option value="US">United States</option>
                                    <option value="CA">Canada</option>
                                    <option value="GB">United Kingdom</option>
                                    <option value="AU">Australia</option>
                                </Select>
                            </FormField>
                            <FormField label="Tax ID / VAT Number">
                                <TextInput name="taxId" id="taxId" value={paymentInfo.taxId} onChange={handleChange} />
                            </FormField>
                        </div>

                        <div style={{ display: 'flex', justifyContent: 'flex-end', paddingTop: '20px' }}>
                            <Button type="submit" variant="primary" disabled={saveLoading}>
                                {saveLoading ? 'Saving…' : 'Save Changes'}
                            </Button>
                        </div>
                    </form>
                </Card>
            </div>
        </div>
    );
};

export default PaymentSettings;